import { create } from 'zustand';
const SESSION_KEY = 'perrigo_auth_session';
const loadStoredSession = () => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    }
    catch {
        return null;
    }
};
const savedSession = loadStoredSession();
export const useAuthStore = create((set, get) => ({
    accessToken: null,
    refreshToken: savedSession?.refreshToken ?? null,
    expiresAt: savedSession?.expiresAt ?? null,
    user: savedSession?.user ?? null,
    // isAuthenticated starts false — the access_token lives in memory and is lost on reload.
    // ProtectedRoute will call refreshToken() to restore it when isInitializing is true.
    isAuthenticated: false,
    isInitializing: !!savedSession,
    // Always false at startup — ProtectedRoute fetches from the roles API before rendering.
    permissionsLoaded: false,
    setAuth: (token, expiresAt, user, refreshToken) => {
        const currentRefreshToken = refreshToken ?? useAuthStore.getState().refreshToken ?? '';
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user, expiresAt, refreshToken: currentRefreshToken }));
        set({ accessToken: token, expiresAt, user, isAuthenticated: true, isInitializing: false, ...(refreshToken ? { refreshToken } : {}) });
    },
    restoreUser: (user) => {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user, expiresAt: '', refreshToken: '' }));
        set({ user, isAuthenticated: true, isInitializing: false, permissionsLoaded: true });
    },
    clearAuth: () => {
        sessionStorage.removeItem(SESSION_KEY);
        set({ accessToken: null, refreshToken: null, expiresAt: null, user: null, isAuthenticated: false, isInitializing: false, permissionsLoaded: false });
    },
    setInitializing: (val) => set({ isInitializing: val }),
    setPermissions: (codes) => {
        const { user, refreshToken, expiresAt } = get();
        if (!user)
            return;
        const updatedUser = {
            ...user,
            permissions: codes,
            isSuperAdmin: codes.includes('super_admin'),
        };
        // Persist updated permissions so the stored session reflects the latest role data.
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user: updatedUser, expiresAt: expiresAt ?? '', refreshToken: refreshToken ?? '' }));
        set({ user: updatedUser, permissionsLoaded: true });
    },
    hasPermission: (code) => {
        const { user } = get();
        if (!user)
            return false;
        if (user.isSuperAdmin)
            return true;
        return user.permissions.includes(code);
    },
}));
export const useCurrentUser = () => useAuthStore((s) => s.user);
export const getUserDetailsForApi = () => {
    const { user } = useAuthStore.getState();
    if (!user)
        return { user_id: '', user_name: '', user_email: '' };
    return {
        user_id: user.id,
        user_name: `${user.firstName} ${user.lastName}`,
        user_email: user.email,
    };
};
